// src/utils/timezoneUtils.js
//
// Central timezone utilities for the UTC migration.
//
// Storage convention (post-migration):
//   teachers.availability[].startTime / endTime  — 'HH:MM' in UTC
//   teachers.availability[].dayOfWeek            — UTC weekday name
//   classes.slots[].time                         — 'HH:MM' in UTC
//   classes.slots[].dayOfWeek                    — UTC weekday name
//   sessions.scheduledAt                         — full UTC ISO string
//
// Display convention:
//   All times shown to users are converted via a viewer-selected display timezone.
//   Default = browser timezone (Intl auto-detect). Persisted in localStorage by callers.

// ─── Internal constants ───────────────────────────────────────────────────────

const DAY_NAMES = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
const DAY_INDEX = Object.fromEntries(DAY_NAMES.map((n, i) => [n, i]));

// ─── Internal: UTC offset helper ──────────────────────────────────────────────
// Returns the UTC offset in minutes for a timezone at a specific UTC instant.
// e.g. America/Vancouver in summer → -420 (UTC-7).

function getUTCOffsetMinutes(timezone, utcDate) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour:     'numeric',
      minute:   'numeric',
      hour12:   false,
    }).formatToParts(utcDate);
    const rawH   = parseInt(parts.find(p => p.type === 'hour').value,   10);
    const localH = rawH === 24 ? 0 : rawH;
    const localM = parseInt(parts.find(p => p.type === 'minute').value, 10);
    const utcH   = utcDate.getUTCHours();
    const utcM   = utcDate.getUTCMinutes();
    let offset   = (localH * 60 + localM) - (utcH * 60 + utcM);
    if (offset >  840) offset -= 1440;   // normalize to valid offset range
    if (offset < -720) offset += 1440;
    return offset;
  } catch {
    return 0;
  }
}

// ─── Browser timezone detection ───────────────────────────────────────────────

/**
 * Returns the browser's IANA timezone string.
 * Falls back to 'UTC' if the API is unavailable.
 */
export function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

// ─── HH:MM ↔ UTC conversion ───────────────────────────────────────────────────

/**
 * Convert a local HH:MM time on a named weekday in a given timezone → UTC.
 *
 * Uses the next upcoming occurrence of that weekday to handle DST accurately.
 *
 * @param {string} localTimeStr   'HH:MM'
 * @param {string} localDayName   'monday' | 'tuesday' | …
 * @param {string} localTimezone  IANA timezone, e.g. 'America/Vancouver'
 * @returns {{ utcTime: string, utcDay: string }}
 */
export function timeToUTC(localTimeStr, localDayName, localTimezone) {
  if (!localTimeStr || !localDayName || !localTimezone) {
    return { utcTime: localTimeStr ?? '', utcDay: localDayName ?? 'monday' };
  }
  try {
    const [h, m]   = localTimeStr.split(':').map(Number);
    const localIdx = DAY_INDEX[localDayName] ?? 0;

    // Build a reference UTC instant near the target local time for DST accuracy.
    const now       = new Date();
    const daysUntil = (localIdx - now.getDay() + 7) % 7 || 7;
    const approxUTC = new Date(Date.UTC(
      now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + daysUntil, h, m, 0,
    ));

    const offsetMins = getUTCOffsetMinutes(localTimezone, approxUTC);

    // UTC = local − offset
    const totalLocalMins = localIdx * 1440 + h * 60 + m;
    const totalUTCMins   = totalLocalMins - offsetMins;
    const normalized     = ((totalUTCMins % 10080) + 10080) % 10080; // 10080 = 7 × 1440

    const utcDayIdx    = Math.floor(normalized / 1440);
    const utcMinsInDay = normalized % 1440;
    const utcH         = Math.floor(utcMinsInDay / 60);
    const utcM         = utcMinsInDay % 60;

    return {
      utcTime: `${String(utcH).padStart(2, '0')}:${String(utcM).padStart(2, '0')}`,
      utcDay:  DAY_NAMES[utcDayIdx],
    };
  } catch {
    return { utcTime: localTimeStr, utcDay: localDayName };
  }
}

/**
 * Convert a UTC HH:MM time on a named UTC weekday → a display timezone.
 *
 * Uses the next upcoming occurrence of that UTC weekday for DST accuracy.
 *
 * @param {string} utcTimeStr      'HH:MM' in UTC
 * @param {string} utcDayName      'monday' | …
 * @param {string} displayTimezone IANA timezone
 * @returns {{ localTime: string, localDay: string, dayChanged: boolean }}
 */
export function timeFromUTC(utcTimeStr, utcDayName, displayTimezone) {
  if (!utcTimeStr || !utcDayName || !displayTimezone) {
    return { localTime: utcTimeStr ?? '', localDay: utcDayName ?? 'monday', dayChanged: false };
  }
  try {
    const [h, m]    = utcTimeStr.split(':').map(Number);
    const utcDayIdx = DAY_INDEX[utcDayName] ?? 0;

    const now       = new Date();
    const daysUntil = (utcDayIdx - now.getUTCDay() + 7) % 7 || 7;
    const utcDate   = new Date(Date.UTC(
      now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + daysUntil, h, m, 0,
    ));

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: displayTimezone,
      weekday:  'long',
      hour:     '2-digit',
      minute:   '2-digit',
      hour12:   false,
    }).formatToParts(utcDate);

    const rawH     = parseInt(parts.find(p => p.type === 'hour').value,   10);
    const localH   = rawH === 24 ? 0 : rawH;
    const localM   = parseInt(parts.find(p => p.type === 'minute').value, 10);
    const localDay = parts.find(p => p.type === 'weekday').value.toLowerCase();

    return {
      localTime:  `${String(localH).padStart(2, '0')}:${String(localM).padStart(2, '0')}`,
      localDay,
      dayChanged: localDay !== utcDayName,
    };
  } catch {
    return { localTime: utcTimeStr, localDay: utcDayName, dayChanged: false };
  }
}

// ─── ISO timestamp → display ──────────────────────────────────────────────────

/**
 * Convert a UTC ISO timestamp to a '12:00 PM' display string in the given timezone.
 * Use this for session.scheduledAt display.
 */
export function formatISOForDisplay(isoStr, displayTimezone) {
  if (!isoStr) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: displayTimezone,
      hour:     'numeric',
      minute:   '2-digit',
      hour12:   true,
    }).format(new Date(isoStr));
  } catch {
    return '';
  }
}

/**
 * Convert a UTC HH:MM string to a '12:00 PM' display string in the given timezone.
 * Use this for recurring slot times (cls.time, availability.startTime/endTime).
 * Uses a reference date near today so DST is reflected accurately.
 */
export function formatTimeDisplay(utcTimeStr, displayTimezone = 'UTC') {
  if (!utcTimeStr) return '';
  try {
    const [h, m] = utcTimeStr.split(':').map(Number);
    // Use the most-recent Monday at this UTC time as the reference date.
    // This gives the correct current-DST-period offset without pinning to a fixed year.
    const now             = new Date();
    const daysSinceMonday = (now.getUTCDay() + 6) % 7;
    const refMonday       = new Date(Date.UTC(
      now.getUTCFullYear(), now.getUTCMonth(),
      now.getUTCDate() - daysSinceMonday, h, m, 0,
    ));
    return new Intl.DateTimeFormat('en-US', {
      timeZone: displayTimezone,
      hour:     'numeric',
      minute:   '2-digit',
      hour12:   true,
    }).format(refMonday);
  } catch {
    // Manual fallback
    const [h, m] = utcTimeStr.split(':').map(Number);
    const ampm   = h >= 12 ? 'PM' : 'AM';
    const h12    = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  }
}

/**
 * Convert a UTC ISO timestamp to 'HH:MM' (24-hour) in the given timezone.
 */
export function isoToDisplayTime(isoStr, displayTimezone) {
  if (!isoStr) return '';
  try {
    const d     = new Date(isoStr);
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: displayTimezone,
      hour:     '2-digit',
      minute:   '2-digit',
      hour12:   false,
    }).formatToParts(d);
    const rawH = parseInt(parts.find(p => p.type === 'hour').value,   10);
    const h    = rawH === 24 ? 0 : rawH;
    const m    = parseInt(parts.find(p => p.type === 'minute').value, 10);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  } catch {
    return '';
  }
}

/**
 * Convert a UTC ISO timestamp to a weekday name ('monday') in the given timezone.
 */
export function isoToDisplayDay(isoStr, displayTimezone) {
  if (!isoStr) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: displayTimezone,
      weekday:  'long',
    }).format(new Date(isoStr)).toLowerCase();
  } catch {
    return '';
  }
}

/**
 * Convert a UTC ISO timestamp to 'YYYY-MM-DD' in the given timezone.
 * Uses en-CA locale which formats as YYYY-MM-DD natively.
 */
export function isoToDisplayDate(isoStr, displayTimezone) {
  if (!isoStr) return '';
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: displayTimezone,
      year:     'numeric',
      month:    '2-digit',
      day:      '2-digit',
    }).format(new Date(isoStr));
  } catch {
    return '';
  }
}

/**
 * Customer-facing date: 'Tue 9 Sep 2026' in the given timezone.
 * For parent-facing screens only — admin screens keep isoToDisplayDate's
 * YYYY-MM-DD. Kept in sync with functions/index.js formatFriendlyDate.
 */
export function formatFriendlyDate(isoStr, displayTimezone) {
  if (!isoStr) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: displayTimezone || 'UTC',
      weekday: 'short',
      day:     'numeric',
      month:   'short',
      year:    'numeric',
    }).formatToParts(new Date(isoStr));
    const v = (t) => (parts.find((p) => p.type === t) || {}).value || '';
    return `${v('weekday')} ${v('day')} ${v('month')} ${v('year')}`.trim();
  } catch {
    return '';
  }
}

// ─── Calendar day UTC range ───────────────────────────────────────────────────

/**
 * Return the UTC ISO start/end that bracket one full calendar day as seen in
 * the given display timezone.
 *
 * Example: April 7 in America/Vancouver (UTC-7 in summer):
 *   startISO = '2024-04-07T07:00:00.000Z'
 *   endISO   = '2024-04-08T06:59:59.999Z'
 *
 * This is the range to use for Firestore scheduledAt queries when filtering by
 * a single calendar day.
 *
 * @param {Date}   jsDate          Any JS Date whose calendar day (in displayTimezone) is the target
 * @param {string} displayTimezone IANA timezone
 * @returns {{ startISO: string, endISO: string }}
 */
export function utcRangeForCalendarDay(jsDate, displayTimezone) {
  try {
    // Get 'YYYY-MM-DD' as seen in the display timezone
    const dateStr = new Intl.DateTimeFormat('en-CA', {
      timeZone: displayTimezone,
      year:     'numeric',
      month:    '2-digit',
      day:      '2-digit',
    }).format(jsDate);

    const [y, mo, d] = dateStr.split('-').map(Number);

    // Two-pass midnight calculation to handle DST transition days correctly.
    // Pass 1: estimate local midnight using noon's offset (close but may be off by 1h on DST days).
    const noonUTC           = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0));
    const noonOffsetMins    = getUTCOffsetMinutes(displayTimezone, noonUTC);
    const estimatedMidnight = new Date(Date.UTC(y, mo - 1, d, 0, 0, 0) - noonOffsetMins * 60 * 1000);

    // Pass 2: re-sample the offset at the estimated midnight — this gives the correct
    // offset even when midnight and noon straddle a DST boundary (spring-forward / fall-back).
    const midnightOffsetMins = getUTCOffsetMinutes(displayTimezone, estimatedMidnight);
    const startMs = Date.UTC(y, mo - 1, d, 0, 0, 0) - midnightOffsetMins * 60 * 1000;
    const endMs   = startMs + 24 * 60 * 60 * 1000 - 1; // 23:59:59.999 local

    return {
      startISO: new Date(startMs).toISOString(),
      endISO:   new Date(endMs).toISOString(),
    };
  } catch {
    // Fallback: treat the date as a UTC calendar day
    const d = new Date(jsDate);
    d.setUTCHours(0, 0, 0, 0);
    return {
      startISO: d.toISOString(),
      endISO:   new Date(d.getTime() + 24 * 60 * 60 * 1000 - 1).toISOString(),
    };
  }
}

// ─── Session scheduledAt builder ──────────────────────────────────────────────

/**
 * Build the scheduledAt ISO string for a class session.
 *
 * The slot recurs every utcDayName at utcTimeStr (both UTC). We find the
 * exact UTC instant within the given display-day's UTC window.
 *
 * Returns null only if the slot genuinely doesn't fall within this display day
 * (caller should treat this as a data integrity issue).
 *
 * @param {Date}   displayDate     JS Date identifying the calendar day the admin is viewing
 * @param {string} displayTimezone
 * @param {string} utcDayName      UTC weekday ('monday' …)
 * @param {string} utcTimeStr      UTC time ('HH:MM')
 * @returns {string|null}          UTC ISO string
 */
export function scheduledAtForDisplayDay(displayDate, displayTimezone, utcDayName, utcTimeStr) {
  try {
    const { startISO, endISO } = utcRangeForCalendarDay(displayDate, displayTimezone);
    const rangeStart = new Date(startISO);
    const rangeEnd   = new Date(endISO);
    const [h, m]     = utcTimeStr.split(':').map(Number);
    const targetDay  = DAY_INDEX[utcDayName] ?? 0;

    // A display day spans at most 25 hours (DST fall-back). Check at most
    // 2 consecutive UTC calendar dates to find the matching one.
    for (let offset = 0; offset <= 1; offset++) {
      const refMs = rangeStart.getTime() + offset * 24 * 60 * 60 * 1000;
      const ref   = new Date(refMs);
      const ts    = new Date(Date.UTC(
        ref.getUTCFullYear(), ref.getUTCMonth(), ref.getUTCDate(), h, m, 0, 0,
      ));
      if (ts.getUTCDay() === targetDay && ts >= rangeStart && ts <= rangeEnd) {
        return ts.toISOString();
      }
    }
    return null;
  } catch {
    return null;
  }
}

// ─── Day-of-date helper ───────────────────────────────────────────────────────

/**
 * Return the weekday name ('monday', etc.) for a JS Date as seen in the given timezone.
 * Used by DailyView/WeeklyView/MonthlyView to replace DAYS_OF_WEEK[date.getDay()].
 */
export function dayOfDateInTimezone(jsDate, timezone) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday:  'long',
    }).format(jsDate).toLowerCase();
  } catch {
    return DAY_NAMES[jsDate.getDay()];
  }
}

// ─── Dropdown data ────────────────────────────────────────────────────────────

/**
 * Curated list of common IANA timezones for the entry dropdowns.
 * Components should prepend the browser's detected timezone if it is not
 * already present in this list.
 */
export const COMMON_TIMEZONES = [
  { value: 'America/Vancouver',    label: 'Pacific (Vancouver)'          },
  { value: 'America/Los_Angeles',  label: 'Pacific (Los Angeles)'        },
  { value: 'America/Denver',       label: 'Mountain (Denver)'            },
  { value: 'America/Chicago',      label: 'Central (Chicago)'            },
  { value: 'America/New_York',     label: 'Eastern (New York)'           },
  { value: 'America/Toronto',      label: 'Eastern (Toronto)'            },
  { value: 'America/Halifax',      label: 'Atlantic (Halifax)'           },
  { value: 'Europe/London',        label: 'London (GMT/BST)'             },
  { value: 'Europe/Paris',         label: 'Central Europe (Paris/CET)'   },
  { value: 'Africa/Cairo',         label: 'Cairo (EET/EEST)'             },
  { value: 'Asia/Beirut',          label: 'Beirut (EET/EEST)'            },
  { value: 'Asia/Damascus',        label: 'Damascus (Syria, UTC+3)'      },
  { value: 'Asia/Riyadh',          label: 'Riyadh (AST)'                 },
  { value: 'Asia/Dubai',           label: 'Dubai (GST)'                  },
  { value: 'Asia/Karachi',         label: 'Pakistan (PKT)'               },
  { value: 'Asia/Kolkata',         label: 'India (IST)'                  },
  { value: 'Australia/Perth',      label: 'Perth (AWST, UTC+8)'          },
  { value: 'Australia/Adelaide',   label: 'Adelaide (ACST/ACDT)'         },
  { value: 'Australia/Brisbane',   label: 'Brisbane (AEST, no DST)'      },
  { value: 'Australia/Sydney',     label: 'Sydney/Melbourne (AEST/AEDT)' },
  { value: 'Pacific/Auckland',     label: 'Auckland (NZST/NZDT)'         },
  { value: 'UTC',                  label: 'UTC'                          },
];

/**
 * Tooltip zones shown in TimezoneTooltip (same in TeacherManager and ClassManager).
 * Input is now UTC; each entry is a display conversion target.
 */
export const TOOLTIP_ZONES = [
  { label: 'Pacific',  tz: 'America/Vancouver' },
  { label: 'Mountain', tz: 'America/Denver'    },
  { label: 'Eastern',  tz: 'America/New_York'  },
  { label: 'London',   tz: 'Europe/London'     },
  { label: 'Cairo',    tz: 'Africa/Cairo'      },
  { label: 'Beirut',   tz: 'Asia/Beirut'       },
  { label: 'Damascus', tz: 'Asia/Damascus'     },
];

export const CALCULATOR_ZONES = [
  { label: 'Pacific (Vancouver)',  tz: 'America/Vancouver',  flag: '🇨🇦' },
  { label: 'Eastern (Toronto)',    tz: 'America/Toronto',    flag: '🇨🇦' },
  { label: 'Atlantic (Halifax)',   tz: 'America/Halifax',    flag: '🇨🇦' },
  { label: 'London',               tz: 'Europe/London',      flag: '🇬🇧' },
  { label: 'Cairo',                tz: 'Africa/Cairo',       flag: '🇪🇬' },
  { label: 'Beirut',               tz: 'Asia/Beirut',        flag: '🇱🇧' },
  { label: 'Damascus',             tz: 'Asia/Damascus',      flag: '🇸🇾' },
  { label: 'Casablanca (Morocco)', tz: 'Africa/Casablanca',  flag: '🇲🇦' },
  { label: 'Sydney/Melbourne',     tz: 'Australia/Sydney',   flag: '🇦🇺' },
  { label: 'Auckland',             tz: 'Pacific/Auckland',   flag: '🇳🇿' },
];
